import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { almostWhite, textGray } from '../utils/colors';
import { Button } from './CommonUiComponents';
import localization from '../utils/locale';
import apiRequest from '../utils/apiRequest';

const ChapterReflectionWrapper = styled.div`
  background-color: ${almostWhite};
  padding: 2rem 4rem;

  @media all and (max-width: 64rem) {
    padding: 1.5rem 0;

    > * {
      max-width: 35.625rem;
      margin-left: auto;
      margin-right: auto;
      padding: 0 1.5rem;
    }
  }
`;

const TopSection = styled.div`
  margin-bottom: 2rem;
`;

const ChapterReflectionTitle = styled.span`
  color: ${textGray};
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
`;

const SmallParagraph = styled.p`
  font-weight: 300;
  font-size: 1rem;
`;

const StyledTextArea = styled.textarea`
  font-size: 0.875rem;
  font-style: italic;
  background: transparent;
  font-weight: 300;
  border: 0;
  outline: none;
  resize: none;
  height: 5.8125rem;
  width: 100%;
`;

const StyledTextAreaWrapper = styled.div`
  height: 9.2125rem;
  width: 28.25rem;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.tertiary};
  background: transparent;
  display: block;
  margin-bottom: 1rem;
  padding: 1rem;

  ${props =>
    props.disabled &&
    css`
      opacity: 0.5;
    `};

  @media all and (max-width: 40rem) {
    width: 100%;
  }
`;

const DoneButton = styled.a`
  cursor: pointer;
  display: inline-block;
  color: ${props => props.theme.tertiary};
  text-decoration: none;
  text-transform: uppercase;
  transition: all 0.3s ease-in-out;
  margin: 0 1em 0 0;

  ${props =>
    !props.disabled &&
    css`
      &:hover {
        text-decoration: underline;
      }
    `};

  ${props =>
    props.disabled &&
    css`
      color: #161e21;
      opacity: 0.3;
      cursor: wait;
    `};
`;

const StyledReflectionPart = styled.div`
  margin-bottom: 2rem;
`;

class ReflectionPart extends Component {
  state = {
    textAreaOpen: false,
    answer: this.props.answer || '',
    postingReflection: false,
    busyNotifyingUserWithSave: false,
    busyNotifyingUserWithDelete: false
  };

  static propTypes = {
    question: PropTypes.string.isRequired
  };

  componentWillReceiveProps(nextProps) {
    const { answer } = this.state;

    if (nextProps.answer !== answer) {
      this.setState({ answer: nextProps.answer });
    }
  }

  toggleTextArea = () => {
    this.setState({
      textAreaOpen: !this.state.textAreaOpen
    });
  };

  handleOnChange = e => {
    const { value } = e.target;

    this.setState({
      answer: value
    });
  };

  handlePostReflection = async () => {
    const {
      bookId,
      part,
      chapterId,
      reflectionID,
      reflectionOrder
    } = this.props;
    const { answer } = this.state;

    this.setState({ postingReflection: true, busyNotifyingUserWithSave: true });

    await apiRequest('wordpress', 'reflections/v1/store', 'POST', {
      book: bookId,
      part,
      chapter: chapterId,
      reflectionID,
      reflectionOrder,
      answer
    });

    this.setState({ postingReflection: false });

    setTimeout(() => this.setState({ busyNotifyingUserWithSave: false }), 2000);
  };

  handleDeleteReflection = async () => {
    const {
      bookId,
      part,
      chapterId,
      reflectionID,
      reflectionOrder
    } = this.props;
    this.setState({
      postingReflection: true,
      busyNotifyingUserWithDelete: true
    });

    await apiRequest('wordpress', 'reflections/v1/store', 'POST', {
      book: bookId,
      part,
      chapter: chapterId,
      reflectionID,
      reflectionOrder,
      answer: ''
    });

    this.setState({ postingReflection: false, answer: '' });

    setTimeout(
      () => this.setState({ busyNotifyingUserWithDelete: false }),
      2000
    );
  };

  render() {
    const { question } = this.props;
    const {
      textAreaOpen,
      answer,
      postingReflection,
      busyNotifyingUserWithDelete,
      busyNotifyingUserWithSave
    } = this.state;

    return (
      <StyledReflectionPart>
        <SmallParagraph>{question}</SmallParagraph>
        {textAreaOpen && (
          <StyledTextAreaWrapper disabled={postingReflection}>
            <StyledTextArea
              placeholder={localization.CHAPTER_REFLECTION_PLACEHOLDER}
              autoFocus
              onChange={this.handleOnChange}
              value={answer}
              disabled={postingReflection}
            />
            <DoneButton
              href="#"
              onClick={e => {
                e.preventDefault();
                this.handlePostReflection();
              }}
              disabled={postingReflection}
              dangerouslySetInnerHTML={{
                __html: postingReflection
                  ? 'Lagrer..'
                  : busyNotifyingUserWithSave
                    ? 'Lagret &#x2713;'
                    : 'Lagre'
              }}
            />
            <DoneButton
              href="#"
              onClick={e => {
                e.preventDefault();
                this.handleDeleteReflection();
              }}
              disabled={postingReflection}
              dangerouslySetInnerHTML={{
                __html: postingReflection
                  ? 'Sletter..'
                  : busyNotifyingUserWithDelete
                    ? 'Slettet &#x2713;'
                    : 'Slett'
              }}
            />
          </StyledTextAreaWrapper>
        )}
        <Button transparent onClick={this.toggleTextArea}>
          {textAreaOpen
            ? localization.CLOSE_LABEL
            : localization.REFLECTION_BUTTON_LABEL}
        </Button>
      </StyledReflectionPart>
    );
  }
}

class ChapterReflection extends Component {
  state = {
    myReflections: []
  };

  componentDidMount() {
    this.fetchMyReflections();
  }

  async fetchMyReflections() {
    const response = await apiRequest('wordpress', 'reflections/v1/list');

    this.setState({
      myReflections: response
    });
  }

  render() {
    const { reflections, chapterId, part, reflectionID, bookId } = this.props;
    const { myReflections } = this.state;

    return (
      <ChapterReflectionWrapper>
        <TopSection>
          <ChapterReflectionTitle>
            {localization.CHAPTER_REFLECTION_TITLE}
          </ChapterReflectionTitle>
        </TopSection>

        {reflections.map((item, index) => {
          const findByOrder = myReflections.find(
            item =>
              parseInt(item.question_order, 10) === index + 1 &&
              reflectionID === parseInt(item.reflection_id, 10)
          );

          return (
            <ReflectionPart
              key={index}
              bookId={bookId}
              question={item.question}
              answer={findByOrder ? findByOrder.answer : ''}
              chapterId={chapterId}
              part={part}
              reflectionID={reflectionID}
              reflectionOrder={index + 1}
            />
          );
        })}
      </ChapterReflectionWrapper>
    );
  }
}

ChapterReflection.propTypes = {
  chapterId: PropTypes.number.isRequired,
  part: PropTypes.number.isRequired,
  reflectionID: PropTypes.number.isRequired,
  bookId: PropTypes.number.isRequired,
  reflections: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string.isRequired,
      answer: PropTypes.string
    })
  )
};

export default ChapterReflection;



// WEBPACK FOOTER //
// ./src/components/ChapterReflection.js